import type { Database } from '../database.types';

type DealPhoto = Database['public']['Tables']['deal_photos']['Row'];

const ALLOWED_MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export type BuildPhotoStoragePathResult = {
  /** Storage key relative to the bucket root, e.g. `deals/<dealId>/<uuid>.jpg`. */
  path: string;
  /** Lower-cased extension chosen from the MIME type, e.g. `jpg`. */
  ext: string;
};

/**
 * Build an immutable Storage object key for a deal photo upload.
 *
 * The path embeds a fresh UUID so two consecutive uploads of the same
 * source file always produce different keys — this is what allows
 * `cacheControl: '31536000'` + `upsert: false` to be safe (ADR-012 §C5).
 *
 * The extension is derived from `file.type` (MIME) rather than the
 * original filename: filenames are user-supplied and can claim any
 * extension regardless of content. The bucket's `allowed_mime_types`
 * list is the authoritative gate; this helper just mirrors it.
 */
export function buildPhotoStoragePath(dealId: string, file: File): BuildPhotoStoragePathResult {
  const ext = ALLOWED_MIME_TO_EXT[file.type.toLowerCase()];
  if (!ext) {
    throw new Error(`Unsupported file MIME type: "${file.type}". Expected jpg, png, or webp.`);
  }
  const uuid = crypto.randomUUID();
  return { path: `deals/${dealId}/${uuid}.${ext}`, ext };
}

/**
 * Resolve the public URL for a deal photo.
 *
 * V1 (ADR-012 §C4) stores the public URL directly on `deal_photos.url` at
 * upload time, so this helper returns it as-is. The indirection exists so
 * a future R2 migration can swap the implementation without touching the
 * components that consume photos.
 */
export function getPhotoUrl(photo: Pick<DealPhoto, 'url' | 'storage_key'>): string {
  return photo.url;
}
